/**
 * Articles Service
 * Business logic and API calls for articles
 */

import { BaseService } from './base.service'
import { 
  Article, 
  CreateArticleDTO, 
  UpdateArticleDTO,
  FilterParams,
  ID 
} from '../types'

export class ArticlesService extends BaseService {
  private readonly endpoint = '/api/articles'

  /**
   * Get all articles with optional filters
   */
  async getAll(params?: FilterParams & {
    category_id?: ID
    subcategory_id?: ID
    status?: string
    live_only?: boolean
  }): Promise<Article[]> {
    const queryString = params ? this.buildQueryString(params) : ''
    return this.get<Article[]>(`${this.endpoint}${queryString}`)
  }

  /**
   * Get article by ID
   */
  async getById(id: ID): Promise<Article> {
    return this.get<Article>(`${this.endpoint}/${id}`)
  }

  /**
   * Create new article
   */
  async create(data: CreateArticleDTO): Promise<Article> {
    return this.post<Article, CreateArticleDTO>(this.endpoint, data)
  }

  /**
   * Update article
   */
  async update(id: ID, data: Partial<UpdateArticleDTO>): Promise<Article> {
    return this.put<Article, Partial<UpdateArticleDTO>>(`${this.endpoint}/${id}`, data)
  }

  /**
   * Delete article
   */
  async delete(id: ID): Promise<void> {
    return this.delete<void>(`${this.endpoint}/${id}`)
  }

  /**
   * Reorder article slots
   */
  async reorderSlots(
    categoryId: ID,
    subcategoryId: ID,
    articles: Array<{ article_id: ID; slot_number: number }>
  ): Promise<void> {
    return this.put<void>(
      `${this.endpoint}/slots/reorder/${categoryId}/${subcategoryId}`,
      { articles }
    )
  }

  /**
   * Add article to slot
   */
  async addToSlot(
    categoryId: ID,
    subcategoryId: ID,
    articleId: ID,
    slotNumber: number
  ): Promise<void> {
    return this.post<void>(
      `${this.endpoint}/slots/add/${categoryId}/${subcategoryId}`,
      { article_id: articleId, slot_number: slotNumber }
    )
  }

  /**
   * Replace article in slot
   */
  async replaceSlot(currentArticleId: ID, newArticleId: ID): Promise<void> {
    return this.post<void>(`${this.endpoint}/replace-slot`, {
      currentArticleId,
      newArticleId,
    })
  }
}

// Export singleton instance
export const articlesService = new ArticlesService()

